import type { MarketPoolQuoteSnapshot } from '@morfi/market-math';
import type { GetProfilePortfolioSnapshotRow } from '../../../generated/openapi-types.gen.js';

export type UserPortfolioUnvalidatedValue =
  | object
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined;

export interface UserPortfolioEntityCoordinatesWire {
  readonly slot: string;
  readonly txIndex: number;
  readonly cpiIndex: string;
  readonly sequence: string | null;
}

export interface UserPortfolioPoolIdentityWire {
  readonly poolId: string | null;
  readonly outcomeMint: string | null;
  readonly ammConfigAddress: string | null;
}

export type UserPortfolioPoolEntryWire =
  | {
      readonly status: 'complete';
      readonly snapshot: MarketPoolQuoteSnapshot;
      readonly identity: UserPortfolioPoolIdentityWire;
      readonly coordinates: UserPortfolioEntityCoordinatesWire;
    }
  | {
      readonly status: 'incomplete';
      readonly reasonCode: string;
      readonly coordinates: UserPortfolioEntityCoordinatesWire;
    };

export interface UserPortfolioRecoveryWire {
  readonly stream: 'MORFI_EVENTS';
  readonly streamSequence: string;
  readonly marketSequences: Readonly<Record<string, string>>;
  readonly outcomeSequences: Readonly<Record<string, string>>;
  readonly positionSequences: Readonly<Record<string, string>>;
}

export type UserPortfolioQuoteBalanceWire = GetProfilePortfolioSnapshotRow['quoteBalances'][number];
export type UserPortfolioPositionWire = GetProfilePortfolioSnapshotRow['positions'][number];

export interface UserPortfolioSnapshotWire {
  readonly wallets: GetProfilePortfolioSnapshotRow['wallets'];
  readonly quoteBalances: GetProfilePortfolioSnapshotRow['quoteBalances'];
  readonly positions: GetProfilePortfolioSnapshotRow['positions'];
  readonly poolSnapshotsByMarket: Readonly<Record<string, UserPortfolioPoolEntryWire>>;
  readonly recovery: UserPortfolioRecoveryWire;
}

export function isUserPortfolioSnapshotWire(
  value: UserPortfolioUnvalidatedValue,
): value is UserPortfolioSnapshotWire {
  try {
    return isUserPortfolioSnapshotWireUnchecked(value);
  } catch {
    // SILENT-CATCH: a throwing Proxy is invalid untrusted wire data.
    return false;
  }
}

function isUserPortfolioSnapshotWireUnchecked(
  value: UserPortfolioUnvalidatedValue,
): value is UserPortfolioSnapshotWire {
  if (!isRecord(value) || !isJsonTree(value)) return false;
  if (!Array.isArray(value.wallets) || !value.wallets.every(isNonEmptyString)) return false;
  if (
    !Array.isArray(value.quoteBalances) ||
    !value.quoteBalances.every(isUserPortfolioQuoteBalanceWire)
  ) {
    return false;
  }
  if (!Array.isArray(value.positions) || !value.positions.every(isUserPortfolioPositionWire)) {
    return false;
  }
  if (!isRecord(value.poolSnapshotsByMarket)) return false;
  for (const [market, entry] of Object.entries(value.poolSnapshotsByMarket)) {
    if (!isNonEmptyString(market) || !isUserPortfolioPoolEntryWire(entry, market)) return false;
  }
  return isUserPortfolioRecoveryWire(value.recovery);
}

function isUserPortfolioQuoteBalanceWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (!isRecord(value)) return false;
  return (
    isNonEmptyString(value.wallet) &&
    isNonEmptyString(value.quoteMint) &&
    isUnsignedIntegerString(value.balanceD6) &&
    isUnsignedIntegerString(value.slot) &&
    isNonNegativeSafeInteger(value.txIndex)
  );
}

function isUserPortfolioPositionWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (!isRecord(value)) return false;
  if (
    'balance' in value ||
    'walletQuoteBalances' in value ||
    'authoritativeCurrentPriceD9' in value
  ) {
    return false;
  }
  for (const field of ['positionId', 'wallet', 'market', 'outcomeMint'] as const) {
    if (!isNonEmptyString(value[field])) return false;
  }
  for (const field of [
    'balanceD6',
    'avgEntryPriceD9',
    'costBasisD6',
    'remainingCostBasisGrossD6',
    'remainingCostBasisNetD6',
    'realizedCostBasisGrossD6',
    'realizedCostBasisNetD6',
    'realizedCostBasisD6',
    'realizedPenaltyD6',
    'prizeClaimableD6',
    'prizeClaimedD6',
  ] as const) {
    if (!isUnsignedIntegerString(value[field])) return false;
  }
  for (const field of [
    'claimRightsBalanceD6',
    'realizedPnlGrossD6',
    'realizedPnlNetD6',
    'realizedPnlD6',
    'unrealizedPnlD6',
  ] as const) {
    if (!isSignedIntegerString(value[field])) return false;
  }
  return (
    (value.currentPriceD9 === null || isUnsignedIntegerString(value.currentPriceD9)) &&
    (value.positionType === 'outcome' || value.positionType === 'winner') &&
    isPositionResultState(value.resultState) &&
    isPrizeState(value.prizeState) &&
    isMarketLifecycle(value.lifecycle) &&
    isNonNegativeSafeInteger(value.marketStatus) &&
    isNonNegativeSafeInteger(value.outcomeIndex) &&
    isNonNegativeSafeInteger(value.tradeCount) &&
    typeof value.outcomeTicker === 'string' &&
    typeof value.isOpen === 'boolean' &&
    isNullableString(value.marketTitle) &&
    isNullableString(value.outcomeLabel) &&
    isNullableString(value.outcomeImageUrl) &&
    typeof value.outcomeImageNsfw === 'boolean' &&
    isNullableNonNegativeSafeInteger(value.winningOutcomeIndex) &&
    isNullableIdentityString(value.poolId) &&
    isUserPortfolioEntityCoordinatesWire(value.coordinates)
  );
}

function isUserPortfolioPoolEntryWire(
  value: UserPortfolioUnvalidatedValue,
  market: string,
): boolean {
  if (!isRecord(value) || !isUserPortfolioEntityCoordinatesWire(value.coordinates)) return false;
  if (value.status === 'incomplete') return isNonEmptyString(value.reasonCode);
  if (value.status !== 'complete' || !isRecord(value.identity) || !isRecord(value.snapshot)) {
    return false;
  }
  if (
    !isNullableIdentityString(value.identity.poolId) ||
    !isNullableIdentityString(value.identity.outcomeMint) ||
    !isNullableIdentityString(value.identity.ammConfigAddress)
  ) {
    return false;
  }
  return isMarketPoolQuoteSnapshotWire(value.snapshot, market);
}

function isMarketPoolQuoteSnapshotWire(
  value: Record<string, UserPortfolioUnvalidatedValue>,
  market: string,
): boolean {
  return (
    value.market === market &&
    isUnsignedIntegerString(value.chainSlot) &&
    isUnsignedIntegerString(value.chainUnixTimestampSeconds) &&
    isMarketLifecycle(value.lifecycle) &&
    isNullableNonNegativeSafeInteger(value.winningOutcomeIndex) &&
    (value.bondingCurvesClosed === null || typeof value.bondingCurvesClosed === 'boolean') &&
    isNullableNonNegativeSafeInteger(value.buyFeeBps) &&
    isNullableNonNegativeSafeInteger(value.sellBaseFeeBps) &&
    isNullableNonNegativeSafeInteger(value.sellPenaltyFeeBps) &&
    isNullableNonNegativeSafeInteger(value.prizePoolFeeBps) &&
    isNullableUnsignedIntegerString(value.minimumQuoteTradeD6) &&
    isBondingCurveTradeGateWire(value.bondingCurveTradeGate) &&
    Array.isArray(value.outcomes) &&
    value.outcomes.every(isOutcomePoolSnapshotWire)
  );
}

function isBondingCurveTradeGateWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (value === null) return true;
  if (!isRecord(value) || !Array.isArray(value.outcomeConvictionPpb)) return false;
  return (
    value.outcomeConvictionPpb.every(isUnsignedIntegerString) &&
    isUnsignedIntegerString(value.lastConvictionUpdateUnixSeconds) &&
    isUnsignedIntegerString(value.convictionBaselineReserveQuoteD6) &&
    isUnsignedIntegerString(value.convictionRetentionPerSecondPpb) &&
    isUnsignedIntegerString(value.convictionResolutionThresholdPpb) &&
    isUnsignedIntegerString(value.convictionReferenceMarginPpb)
  );
}

function isOutcomePoolSnapshotWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (!isRecord(value)) return false;
  return (
    isNonNegativeSafeInteger(value.outcomeIndex) &&
    isNonEmptyString(value.outcomeMint) &&
    isNullableUnsignedIntegerString(value.realQuoteReserveD6) &&
    isBondingCurveSnapshotWire(value.bondingCurve) &&
    isCpSwapSnapshotWire(value.cpSwap)
  );
}

function isBondingCurveSnapshotWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  return (
    isUnsignedIntegerString(value.virtualQuoteReserveD6) &&
    isUnsignedIntegerString(value.poolQuoteReserveD6) &&
    isUnsignedIntegerString(value.initialOutcomeSupplyD6) &&
    isUnsignedIntegerString(value.poolOutcomeReserveD6) &&
    typeof value.closedForTrade === 'boolean'
  );
}

function isCpSwapSnapshotWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  return (
    isUnsignedIntegerString(value.quoteReserveD6) &&
    isUnsignedIntegerString(value.outcomeReserveD6) &&
    isUnsignedIntegerString(value.tradeFeeRateMillionths) &&
    isUnsignedIntegerString(value.creatorFeeRateMillionths) &&
    typeof value.creatorFeeEnabled === 'boolean' &&
    (value.creatorFeeOn === 'both' ||
      value.creatorFeeOn === 'token0' ||
      value.creatorFeeOn === 'token1') &&
    (value.outcomeTokenIndex === 0 || value.outcomeTokenIndex === 1) &&
    typeof value.swapEnabled === 'boolean' &&
    isUnsignedIntegerString(value.openTimeUnixSeconds)
  );
}

function isUserPortfolioRecoveryWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (!isRecord(value)) return false;
  return (
    value.stream === 'MORFI_EVENTS' &&
    isUnsignedIntegerString(value.streamSequence) &&
    isSequenceRecord(value.marketSequences) &&
    isSequenceRecord(value.outcomeSequences) &&
    isSequenceRecord(value.positionSequences)
  );
}

function isUserPortfolioEntityCoordinatesWire(value: UserPortfolioUnvalidatedValue): boolean {
  if (!isRecord(value)) return false;
  return (
    isUnsignedIntegerString(value.slot) &&
    isNonNegativeSafeInteger(value.txIndex) &&
    isUnsignedIntegerString(value.cpiIndex) &&
    (value.sequence === null || isNonEmptyString(value.sequence))
  );
}

function isSequenceRecord(value: UserPortfolioUnvalidatedValue): boolean {
  if (!isRecord(value)) return false;
  return Object.entries(value).every(
    ([key, sequence]) => isNonEmptyString(key) && isNonEmptyString(sequence),
  );
}

function isJsonTree(value: UserPortfolioUnvalidatedValue, seen: Set<object> = new Set()): boolean {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  ) {
    return true;
  }
  if (typeof value !== 'object' || seen.has(value)) return false;
  return Array.isArray(value) ? isJsonArray(value, seen) : isJsonPlainObject(value, seen);
}

function isJsonArray(value: readonly UserPortfolioUnvalidatedValue[], seen: Set<object>): boolean {
  if (Object.getPrototypeOf(value) !== Array.prototype) return false;
  if (Object.getOwnPropertySymbols(value).length > 0) return false;
  const names = Object.getOwnPropertyNames(value);
  if (names.length !== value.length + 1 || !names.includes('length')) return false;
  seen.add(value);
  let valid = true;
  for (let index = 0; index < value.length; index += 1) {
    const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
    if (
      descriptor === undefined ||
      !descriptor.enumerable ||
      !('value' in descriptor) ||
      !isJsonTree(descriptor.value, seen)
    ) {
      valid = false;
      break;
    }
  }
  seen.delete(value);
  return valid;
}

function isJsonPlainObject(value: object, seen: Set<object>): boolean {
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return false;
  if (Object.getOwnPropertySymbols(value).length > 0) return false;
  seen.add(value);
  let valid = true;
  for (const name of Object.getOwnPropertyNames(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, name);
    if (
      descriptor === undefined ||
      !descriptor.enumerable ||
      !('value' in descriptor) ||
      !isJsonTree(descriptor.value, seen)
    ) {
      valid = false;
      break;
    }
  }
  seen.delete(value);
  return valid;
}

function isRecord(
  value: UserPortfolioUnvalidatedValue,
): value is Record<string, UserPortfolioUnvalidatedValue> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value: UserPortfolioUnvalidatedValue): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isNullableString(value: UserPortfolioUnvalidatedValue): value is string | null {
  return value === null || typeof value === 'string';
}

function isNullableIdentityString(value: UserPortfolioUnvalidatedValue): value is string | null {
  return value === null || isNonEmptyString(value);
}

function isUnsignedIntegerString(value: UserPortfolioUnvalidatedValue): value is string {
  return typeof value === 'string' && /^(?:0|[1-9]\d*)$/.test(value);
}

function isSignedIntegerString(value: UserPortfolioUnvalidatedValue): value is string {
  return typeof value === 'string' && /^(?:0|-?[1-9]\d*)$/.test(value);
}

function isNullableUnsignedIntegerString(
  value: UserPortfolioUnvalidatedValue,
): value is string | null {
  return value === null || isUnsignedIntegerString(value);
}

function isNullableNonNegativeSafeInteger(
  value: UserPortfolioUnvalidatedValue,
): value is number | null {
  return value === null || isNonNegativeSafeInteger(value);
}

function isMarketLifecycle(
  value: UserPortfolioUnvalidatedValue,
): value is 'created' | 'backing' | 'trading' | 'resolved' | 'graduated' {
  return (
    value === 'created' ||
    value === 'backing' ||
    value === 'trading' ||
    value === 'resolved' ||
    value === 'graduated'
  );
}

function isPositionResultState(
  value: UserPortfolioUnvalidatedValue,
): value is 'open' | 'won_holding' | 'won_sold' | 'lost' | 'sold_out' {
  return (
    value === 'open' ||
    value === 'won_holding' ||
    value === 'won_sold' ||
    value === 'lost' ||
    value === 'sold_out'
  );
}

function isPrizeState(
  value: UserPortfolioUnvalidatedValue,
): value is 'none' | 'claimable' | 'claimed' {
  return value === 'none' || value === 'claimable' || value === 'claimed';
}

function isNonNegativeSafeInteger(value: UserPortfolioUnvalidatedValue): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}
